import { EMPTY_STRING } from '@rotomeca/utils';
import { HTMLBnumCardItem } from '../card-item/bnum-card-item';
import { JsCircularEvent } from '@rotomeca/event';
import { HTMLBnumDate } from '../../atoms/date/bnum-date';
import { EVENT_DEFAULT } from '../../../core/utils/constants/constants';
import { Nullable } from '@rotomeca/utils';
import { CapitalizeLine } from '@rotomeca/utils';
import { TAG_CARD_ITEM_AGENDA } from '../../../core/utils/constants/tags';
import style from './bnum-card-item-agenda.less';
import { HTMLBnumIcon } from '../../atoms/icon/bnum-icon';
import { BnumConfig } from '../../../core/utils/BnumConfig';
import { Scheduler } from '../../../core/utils/Scheduler';
import { Data, Define, NO_SETTER, Self } from '../../../core/decorators';
import { CustomDom } from '../../bnum-element';
import { BnumDateUtils } from '../../../core/utils/BnumDateUtils';
import {
	AgendaEvent,
	CreateConstructorOptions,
	FromEventConstructorOptions,
	HTMLBnumCardItemAgendaUI,
	OnStartDefineActionCallback,
	UnknownDateElement,
} from './card-item-agenda.internal/types';
import { HTMLBnumFragment } from 'components/atoms';
import {
	NO_DEFAULT,
	render,
} from '../card-item/bnum-card-item.internal/template';
import {
	ATTRIBUTE_ALL_DAY,
	ATTRIBUTE_DATA_LOCATION,
	ATTRIBUTE_DATA_TITLE,
	ATTRIBUTE_MODE,
	ATTRIBUTE_PRIVATE,
	CLASS_ACTION,
	CLASS_ACTION_OVERRIDE,
	CLASS_ALL_DAY,
	CLASS_BLOCK,
	CLASS_DAY,
	CLASS_HORIZONTAL,
	CLASS_HOUR,
	CLASS_LOCATION,
	CLASS_LOCATION_OVERRIDE,
	CLASS_PRIVATE_ICON,
	CLASS_TITLE,
	CLASS_TITLE_OVERRIDE,
	CLASS_VERTICAL,
	SLOT_ACTION,
	SLOT_LOCATION,
	SLOT_TITLE,
	STATE_ACTION_DEFINED,
	STATE_ALL_DAY,
	STATE_MODE_PREFIX,
	STATE_NO_LOCATION,
	STATE_PRIVATE,
} from './card-item-agenda.internal/constants';
import { UI } from 'core/decorators/ui';
import { Listener, NoInitListener } from 'core/decorators/listener';

export {
	AgendaEvent,
	CreateConstructorOptions,
	FromEventConstructorOptions,
	OnStartDefineActionCallback,
	UnknownDateElement,
};

//#region Global Constants
const SHEET = HTMLBnumCardItem.ConstructCSSStyleSheet(style);
/** Format par défaut pour la date (ex: 2024-01-01) */
const FORMAT_DATE_DEFAULT = 'yyyy-MM-dd';
/** Format par défaut pour la date et l'heure (ex: 2024-01-01 08:00:00) */
const FORMAT_DATE_TIME_DEFAULT = 'yyyy-MM-dd HH:mm';
/** Format par défaut pour l'heure (ex: 08:00) */
const FORMAT_HOUR_DEFAULT = 'HH:mm';
/** Format pour l'heure si le jour est différent (ex: 20/11) */
const FORMAT_HOUR_DIFF_DAY = 'dd/MM';
/** Texte pour "Aujourd'hui" (localisé) */
const FORMAT_TODAY = BnumConfig.Get('local_keys')!.today!;
/** Texte pour "Demain" (localisé) */
const FORMAT_TOMORROW = BnumConfig.Get('local_keys')!.tomorrow!;
/** Format pour la date d'événement (ex: lundi 20 novembre) */
const FORMAT_EVENT_DATE = 'EEEE dd MMMM';
const ICON_PRIVATE = 'lock';
/** Texte affiché pour "toute la journée" (localisé) */
const TEXT_ALL_DAY = BnumConfig.Get('local_keys')!.day!;
/** Attribut d'état interne pour la gestion du rendu différé */
const ATTRIBUTE_PENDING = 'agenda_all';
/** Mode par défaut */
const MODE_DEFAULT = 'default';
/** Symbole pour la réinitialisation interne */
const SYMBOL_RESET = Symbol('reset');
/** Attribut pour les autres modes */
const ATTRIBUTE_OTHER_MODES = 'other-modes';
//#endregion Global Constants

//#region Template
const AGENDA = (
	<HTMLBnumFragment>
		<span class={CLASS_DAY + ' bold'}></span>
		<div class={CLASS_HORIZONTAL}>
			<div class={CLASS_BLOCK}>
				<span class={CLASS_HOUR + ' bold'}></span>
				<div class={CLASS_VERTICAL}>
					<span class={CLASS_TITLE + ' bold-500'}>
						<slot id={SLOT_TITLE} name={SLOT_TITLE}></slot>
						<div class={CLASS_TITLE_OVERRIDE} hidden></div>
					</span>
					<span class={CLASS_LOCATION}>
						<slot id={SLOT_LOCATION} name={SLOT_LOCATION}></slot>
						<div class={CLASS_LOCATION_OVERRIDE} hidden></div>
					</span>
				</div>
			</div>
			<span class={CLASS_ACTION}>
				<slot id={SLOT_ACTION} name={SLOT_ACTION}></slot>
				<div class={CLASS_ACTION_OVERRIDE} hidden></div>
			</span>
		</div>
		<HTMLBnumIcon class={CLASS_PRIVATE_ICON} hidden>
			{ICON_PRIVATE}
		</HTMLBnumIcon>
	</HTMLBnumFragment>
);
//#endregion Template

/**
 * Item de carte agenda
 *
 * @category Card
 *
 * @structure Initalisation basique
 * <bnum-card-item-agenda
 * data-date="2024-01-01"
 * data-start-date="2024-01-01 08:00:00"
 * data-end-date="2024-01-01 10:00:00"
 * data-title="Réunion de projet"
 * data-location="Salle de conférence">
 * </bnum-card-item-agenda>
 *
 * @structure Exemple avec des dates de départs et fin différentes du jour de base
 * <bnum-card-item-agenda
 * data-date="2025-11-20"
 * data-start-date="2025-10-20 09:40:00"
 * data-end-date="2025-12-20 10:10:00"
 * data-title="Réunion de projet"
 * data-location="Salle de conférence">
 * </bnum-card-item-agenda>
 *
 * @structure Exemple de journée entière
 * <bnum-card-item-agenda all-day
 * data-date="2025-11-21"
 * data-title="Télétravail"
 * data-location="A la maison">
 * </bnum-card-item-agenda>
 *
 *
 * @structure Exemple avec des slots
 * <bnum-card-item-agenda
 * data-date="2025-11-20"
 * data-start-date="2025-11-20 09:40:00"
 * data-end-date="2025-11-20 10:10:00">
 * <span slot="title">Réunion de projet avec l'équipe marketing</span>
 * <span slot="location">Salle de conférence, Bâtiment A</span>
 * <bnum-primary-button slot="action" rounded data-icon='video_camera_front' data-icon-margin="0" onclick="alert('Action déclenchée !')"></bnum-primary-button>
 * </bnum-card-item-agenda>
 *
 * @structure Exemple de journée privée
 * <bnum-card-item-agenda all-day private
 * data-date="2025-11-21"
 * data-title="Télétravail"
 * data-location="A la maison">
 * </bnum-card-item-agenda>
 *
 * @structure Exemple de journée avec un mode
 * <bnum-card-item-agenda all-day mode="telework"
 * data-date="2025-11-21"
 * data-title="Télétravail"
 * data-location="A la maison">
 * </bnum-card-item-agenda>
 *
 * @slot title - Contenu du titre de l'événement
 * @slot location - Contenu du lieu de l'événement
 * @slot action - Contenu de l'action de l'événement (bouton etc...)
 *
 * @state no-location - Actif quand le lieu n'est pas défini
 * @state all-day - Actif quand l'événement dure toute la journée
 * @state private - Actif quand l'événement est privé
 * @state mode-X - Actif quand le mode de l'événement est défini à "X" (remplacer X par le mode)
 * @state action - Actif quand une action est définie pour l'événement
 *
 * @attr {boolean | string | undefined} (optional) (default: undefined) all-day - Indique si l'événement dure toute la journée
 * @attr {boolean | string | undefined} (optional) (default: undefined) private - Indique si l'événement est privé
 * @attr {string | undefined} (optional) (default: undefined) mode - Indique le mode de l'événement et permet des affichages visuels (custom ou non) en fonction de celui-ci. Créer l'état CSS `mode-X`.
 * @attr {string | undefined} (optional) (default: undefined) data-title - Titre de l'événement
 * @attr {string | undefined} (optional) (default: undefined) data-location - Lieu de l'événement
 * @attr {string | undefined} data-date - Date de base de l'événement
 * @attr {string | undefined} (optional) (default: yyyy-MM-dd) data-date-format - Format de la date de base de l'événement
 * @attr {string | undefined} data-start-date - Date de début de l'événement
 * @attr {string | undefined} (optional) (default: yyyy-MM-dd HH:mm:ss) data-start-date-format - Format de la date de début de l'événement
 * @attr {string | undefined} data-end-date - Date de fin de l'événement
 * @attr {string | undefined} (optional) (default: yyyy-MM-dd HH:mm:ss) data-end-date-format - Format de la date de fin de l'événement
 *
 * @cssvar {var(--bnum-space-s, 8px)} --bnum-card-item-agenda-gap - Contrôle l'espacement général entre les éléments du composant.
 * @cssvar {var(--bnum-font-weight-bold, 700)} --bnum-card-item-agenda-date-bold - Poids de police pour les textes en gras (date).
 * @cssvar {var(--bnum-font-weight-medium, 500)} --bnum-card-item-agenda-date-bold-medium - Poids de police medium pour certains textes.
 * @cssvar {var(--bnum-space-s, 8px)} --bnum-card-item-agenda-padding-right-hour - Padding à droite de l'heure.
 * @cssvar {0} --bnum-card-item-agenda-padding-left-hour - Padding à gauche de l'heure.
 * @cssvar {0} --bnum-card-item-agenda-padding-top-hour - Padding en haut de l'heure.
 * @cssvar {0} --bnum-card-item-agenda-padding-bottom-hour - Padding en bas de l'heure.
 * @cssvar {var(--bnum-border-surface, 1px solid #E0E0E0)} --bnum-card-item-agenda-date-border-right - Bordure à droite de l'heure.
 * @cssvar {none} --bnum-card-item-agenda-date-border-left - Bordure à gauche de l'heure.
 * @cssvar {none} --bnum-card-item-agenda-date-border-top - Bordure en haut de l'heure.
 * @cssvar {none} --bnum-card-item-agenda-date-border-bottom - Bordure en bas de l'heure.
 * @cssvar {var(--bnum-font-size-xs, 12px)} --bnum-card-item-agenda-location-font-size - Taille de police pour le lieu.
 * @cssvar {var(--bnum-space-s, 8px)} --bnum-card-item-agenda-private-icon-top - Position top de l'icône privée.
 * @cssvar {var(--bnum-space-s, 8px)} --bnum-card-item-agenda-private-icon-right - Position right de l'icône privée.
 * @cssvar {italic} --bnum-card-item-agenda-telework-font-style - Style de police en mode télétravail.
 * @cssvar {'\e88a'} --bnum-card-item-agenda-telework-icon-content - Contenu de l'icône télétravail.
 * @cssvar {var(--bnum-icon-font-family, 'Material Symbols Outlined')} --bnum-card-item-agenda-telework-icon-font-family - Famille de police de l'icône télétravail.
 * @cssvar {var(--bnum-font-size-xxl, 32px)} --bnum-card-item-agenda-telework-icon-font-size - Taille de l'icône télétravail.
 * @cssvar {var(--bnum-space-s, 8px)} --bnum-card-item-agenda-telework-icon-bottom - Position bottom de l'icône télétravail.
 * @cssvar {var(--bnum-space-s, 8px)} --bnum-card-item-agenda-telework-icon-right - Position right de l'icône télétravail.
 * @cssvar {20px} --bnum-card-item-agenda-telework-action-margin-right - Marge à droite de l'action en mode télétravail.
 */
@Define({
	tag: TAG_CARD_ITEM_AGENDA,
	template: render(AGENDA, NO_DEFAULT),
})
export class HTMLBnumCardItemAgenda extends HTMLBnumCardItem {
	//#region Private Fields
	#_sd: Nullable<Date> = null;
	#_ed: Nullable<Date> = null;
	#_bd: Nullable<Date> = null;
	#_pr: Nullable<boolean> = null;
	#_spanAllday: Nullable<HTMLSpanElement> = null;
	#_bnumDateStart: Nullable<HTMLBnumDate> = null;
	#_bnumDateEnd: Nullable<HTMLBnumDate> = null;
	#_shedulerTitle: Nullable<Scheduler<HTMLElement | string | symbol>> = null;
	#_shedulerLocation: Nullable<Scheduler<HTMLElement | string | symbol>> = null;
	#_shedulerAction: Nullable<Scheduler<HTMLElement | symbol>> = null;
	//#endregion
	//#region Public Fields
	//#endregion
	//#region Getters/Setters
	@UI({
		slotTitle: `#${SLOT_TITLE}`,
		slotLocation: `#${SLOT_LOCATION}`,
		slotAction: `#${SLOT_ACTION}`,
		spanDate: `.${CLASS_DAY}`,
		spanHour: `.${CLASS_HOUR}`,
		overrideTitle: `.${CLASS_TITLE_OVERRIDE}`,
		overrideLocation: `.${CLASS_LOCATION_OVERRIDE}`,
		overrideAction: `.${CLASS_ACTION_OVERRIDE}`,
		privateIcon: `.${CLASS_PRIVATE_ICON}`,
	})
	accessor #_ui!: HTMLBnumCardItemAgendaUI;

	/**
	 * Événement circulaire déclenché lors de la définition de l'action.
	 * Permet de personnaliser l'action affichée dans la carte agenda.
	 */
	@Listener(NoInitListener, { circular: true })
	accessor onstartdefineaction!: JsCircularEvent<OnStartDefineActionCallback>;

	@Data('date', NO_SETTER) accessor #_baseDate: string = EMPTY_STRING;
	@Data('date-format', NO_SETTER) accessor #_baseDateFormat: string =
		FORMAT_DATE_DEFAULT;
	@Data('start-date', NO_SETTER) accessor #_startDate: string = EMPTY_STRING;
	@Data('start-date-format', NO_SETTER) accessor #_startDateFormat: string =
		FORMAT_DATE_TIME_DEFAULT;
	@Data('end-date', NO_SETTER) accessor #_endDate: string = EMPTY_STRING;
	@Data('end-date-format', NO_SETTER) accessor #_endDateFormat: string =
		FORMAT_DATE_TIME_DEFAULT;
	@Data(NO_SETTER) accessor #_title: Nullable<string> = null;
	@Data(NO_SETTER) accessor #_location: Nullable<string> = null;

	/**
	 * Indique si l'événement dure toute la journée.
	 */
	get isAllDay(): boolean {
		return this.hasAttribute(ATTRIBUTE_ALL_DAY);
	}
	/**
	 * Date de base de l'événement (jour affiché).
	 */
	get baseDate(): Date {
		return (
			this.#_bd ??
			BnumDateUtils.parse(this.#_baseDate, this.#_baseDateFormat) ??
			new Date()
		);
	}
	set baseDate(value: Date | string) {
		if (typeof value === 'string') {
			value = BnumDateUtils.parse(value, this.#_baseDateFormat) ?? new Date();
		}
		const oldValue = this.#_bd;
		this.#_bd = value;
		this.#_bnumDateStart?.askRender?.();
		this.#_bnumDateEnd?.askRender?.();
		this._p_addPendingAttribute(
			ATTRIBUTE_PENDING,
			oldValue === null
				? null
				: BnumDateUtils.format(
						oldValue,
						BnumDateUtils.getOptionsFromToken(this.#_baseDateFormat),
					),
			BnumDateUtils.format(
				value,
				BnumDateUtils.getOptionsFromToken(this.#_baseDateFormat),
			),
		)._p_requestAttributeUpdate();
	}
	/**
	 * Date de début de l'événement.
	 */
	get startDate(): Date {
		return (
			this.#_sd ??
			BnumDateUtils.parse(this.#_startDate, this.#_startDateFormat) ??
			new Date()
		);
	}
	set startDate(value: Date | string) {
		if (typeof value === 'string') {
			value = BnumDateUtils.parse(value, this.#_startDateFormat) ?? new Date();
		}
		const oldValue = this.#_sd;
		this.#_sd = value;
		this.#_bnumDateEnd?.askRender?.();
		this._p_addPendingAttribute(
			ATTRIBUTE_PENDING,
			oldValue === null
				? null
				: BnumDateUtils.format(
						oldValue,
						BnumDateUtils.getOptionsFromToken(this.#_startDateFormat),
					),
			BnumDateUtils.format(
				value,
				BnumDateUtils.getOptionsFromToken(this.#_startDateFormat),
			),
		)._p_requestAttributeUpdate();
	}
	/**
	 * Date de fin de l'événement.
	 */
	get endDate(): Date {
		return (
			this.#_ed ??
			BnumDateUtils.parse(this.#_endDate, this.#_endDateFormat) ??
			new Date()
		);
	}
	set endDate(value: Date | string) {
		if (typeof value === 'string') {
			value = BnumDateUtils.parse(value, this.#_endDateFormat) ?? new Date();
		}
		const oldValue = this.#_ed;
		this.#_ed = value;
		this.#_bnumDateStart?.askRender?.();
		this._p_addPendingAttribute(
			ATTRIBUTE_PENDING,
			oldValue === null
				? null
				: BnumDateUtils.format(
						oldValue,
						BnumDateUtils.getOptionsFromToken(this.#_endDateFormat),
					),
			BnumDateUtils.format(
				value,
				BnumDateUtils.getOptionsFromToken(this.#_endDateFormat),
			),
		)._p_requestAttributeUpdate();
	}
	get private(): boolean {
		return this.#_pr ?? this.#_private;
	}
	set private(value: boolean) {
		const oldValue = this.#_pr;
		this.#_pr = value;

		this._p_addPendingAttribute(
			ATTRIBUTE_PENDING,
			JSON.stringify(oldValue),
			JSON.stringify(value),
		)._p_requestAttributeUpdate();
	}
	get #_private(): boolean {
		return this.hasAttribute(ATTRIBUTE_PRIVATE);
	}
	get #_getMode(): string {
		return this.getAttribute(ATTRIBUTE_MODE) || MODE_DEFAULT;
	}

	/**
	 * Récupère les autres modes du composant.
	 */
	get otherModes(): string[] {
		return this.getAttribute(ATTRIBUTE_OTHER_MODES)?.split(',') || [];
	}

	//#endregion

	constructor() {
		super();
	}

	//#region Lifecycle Hooks
	/**
	 * Récupère le style CSS à appliquer au composant.
	 * @returns Chaîne de style CSS à appliquer au composant.
	 */
	protected _p_getStylesheets(): CSSStyleSheet[] {
		return [...super._p_getStylesheets(), SHEET];
	}

	/**
	 * Précharge les données nécessaires à l'initialisation du composant.
	 */
	protected _p_preload(): void {
		super._p_preload();

		this.#_sd = this.startDate;
		this.#_ed = this.endDate;
	}

	protected _p_buildDOM(container: CustomDom): void {
		super._p_buildDOM(container);

		const dateHtml = this.#_generateDateHtml(new Date());
		this.#_ui.spanDate.appendChild(dateHtml);

		this.#_bnumDateStart = this.setHourLogic(HTMLBnumDate.Create(new Date()));
		this.#_bnumDateEnd = this.setHourLogic(HTMLBnumDate.Create(new Date()));

		this.#_spanAllday = this._p_createSpan({
			classes: [CLASS_ALL_DAY],
			child: TEXT_ALL_DAY,
		});
		this.#_spanAllday.hidden = true;

		this.#_ui.spanHour.append(
			this.#_bnumDateStart,
			this.#_bnumDateEnd,
			this.#_spanAllday,
		);
	}

	/**
	 * Attache le composant au DOM et initialise les valeurs par défaut.
	 */
	protected _p_attach(): void {
		super._p_attach();

		if (this._p_slot) this._p_slot.hidden = true;

		if (this.#_title) {
			const defaultTitle: Text = document.createTextNode(this.#_title);
			this.#_ui.slotTitle.appendChild(defaultTitle);
		}

		if (this.#_location) {
			const defaultLocation: Text = document.createTextNode(this.#_location);
			this.#_ui.slotLocation.appendChild(defaultLocation);
		}

		this.#_renderDOM();
		this.#_release();
	}

	/**
	 * Libère les attributs data- utilisés pour l'initialisation.
	 */
	#_release(): void {
		void this.#_startDate;
		void this.#_endDate;
		void this.#_startDateFormat;
		void this.#_endDateFormat;
		void this.#_baseDate;
		void this.#_baseDateFormat;
	}

	/**
	 * Met à jour le rendu du composant.
	 */
	protected _p_render() {
		super._p_render();

		this.#_renderDOM();
	}

	/**
	 * Met à jour l'affichage du composant selon les données courantes.
	 */
	#_renderDOM(): void {
		let createDate = true;

		this._p_addState(`${STATE_MODE_PREFIX}${this.#_getMode}`);

		if (this.otherModes.length > 0) {
			this.otherModes.forEach(mode => {
				this._p_addState(`other-${STATE_MODE_PREFIX}${mode}`);
			});
		}

		// Gestion des slots
		if (this.#_isSlotLocationEmpty()) this._p_addState(STATE_NO_LOCATION);

		// Gestion de l'action
		const eventResult = this.onstartdefineaction.call({
			location: this.#_isSlotLocationEmpty()
				? this.#_location || EMPTY_STRING
				: this.#_ui.slotLocation.textContent || EMPTY_STRING,
			action: undefined,
		});

		if (eventResult.action) {
			this.updateAction(eventResult.action, { forceCall: true });
		}

		if (
			eventResult.action ||
			this.#_ui.overrideAction.hidden === false ||
			(this.#_ui.slotAction && this.#_ui.slotAction.children.length > 0)
		) {
			this._p_addState(STATE_ACTION_DEFINED);
		}

		if (this.#_ui.spanDate && this.#_ui.spanDate.children.length > 0) {
			const dateHtml: Nullable<HTMLBnumDate> = this.shadowRoot!.querySelector(
				HTMLBnumDate.TAG,
			);

			if (dateHtml !== null) {
				createDate = false;
				dateHtml.date = this.baseDate;
			}
		}

		if (createDate) {
			const dateHtml: HTMLBnumDate = this.#_generateDateHtml(this.baseDate);
			this.#_ui.spanDate.appendChild(dateHtml);
		}

		// Gestion de la date
		if (this.isAllDay) {
			if (this.#_bnumDateStart !== null) this.#_bnumDateStart.hidden = true;
			if (this.#_bnumDateEnd !== null) this.#_bnumDateEnd.hidden = true;

			if (this.#_spanAllday === null) {
				this._p_addState(STATE_ALL_DAY);
				const spanAllDay: HTMLSpanElement = this._p_createSpan({
					classes: [CLASS_ALL_DAY],
					child: TEXT_ALL_DAY,
				});
				this.#_spanAllday = spanAllDay;
				this.#_ui.spanHour.appendChild(spanAllDay);
			} else this.#_spanAllday.hidden = false;
		} else {
			if (this.#_spanAllday !== null) this.#_spanAllday.hidden = true;

			if (this.#_bnumDateStart === null && this.#_bnumDateEnd === null) {
				const htmlStartDate: HTMLBnumDate = this.setHourLogic(
					HTMLBnumDate.Create(this.startDate),
				);
				const htmlEndDate: HTMLBnumDate = this.setHourLogic(
					HTMLBnumDate.Create(this.endDate),
				);

				this.#_bnumDateStart = htmlStartDate;
				this.#_bnumDateEnd = htmlEndDate;

				this.#_ui.spanHour.append(htmlStartDate, htmlEndDate);
			} else {
				this.#_bnumDateStart!.hidden = false;
				this.#_bnumDateEnd!.hidden = false;
				this.#_bnumDateStart!.date = this.startDate;
				this.#_bnumDateEnd!.date = this.endDate;
			}
		}

		if (this.#_private) {
			this._p_addState(STATE_PRIVATE);
			if (this.#_ui.privateIcon === null) {
				this.#_ui.privateIcon =
					HTMLBnumIcon.Create(ICON_PRIVATE).addClass(CLASS_PRIVATE_ICON);
				this.shadowRoot!.appendChild(this.#_ui.privateIcon);
			} else this.#_ui.privateIcon.hidden = false;
		} else if (this.#_ui.privateIcon) this.#_ui.privateIcon.hidden = true;
	}

	//#endregion

	//#region Public Methods
	/**
	 * Met à jour l'action affichée dans la carte agenda.
	 * @param element Élément HTML à afficher comme action
	 * @returns L'instance du composant
	 */
	public updateAction(
		element: HTMLElement,
		{ forceCall = false }: { forceCall?: boolean } = {},
	): HTMLBnumCardItemAgenda {
		return this.#_requestShedulerAction(element, { forceCall });
	}

	/**
	 * Réinitialise l'action à sa valeur par défaut.
	 * @returns L'instance du composant
	 */
	public resetAction(): HTMLBnumCardItemAgenda {
		return this.#_requestShedulerAction(SYMBOL_RESET);
	}

	/**
	 * Met à jour le titre affiché dans la carte agenda.
	 * @param element Élément HTML ou texte à afficher comme titre
	 * @returns L'instance du composant
	 */
	public updateTitle(element: HTMLElement): HTMLBnumCardItemAgenda;
	public updateTitle(element: string): HTMLBnumCardItemAgenda;
	public updateTitle(element: HTMLElement | string): HTMLBnumCardItemAgenda {
		return this.#_requestShedulerTitle(element);
	}

	/**
	 * Réinitialise le titre à sa valeur par défaut.
	 * @returns L'instance du composant
	 */
	public resetTitle(): HTMLBnumCardItemAgenda {
		return this.#_requestShedulerTitle(SYMBOL_RESET);
	}

	/**
	 * Met à jour le lieu affiché dans la carte agenda.
	 * @param element Élément HTML ou texte à afficher comme lieu
	 * @returns L'instance du composant
	 */
	public updateLocation(element: HTMLElement): HTMLBnumCardItemAgenda;
	public updateLocation(element: string): HTMLBnumCardItemAgenda;
	public updateLocation(element: HTMLElement | string): HTMLBnumCardItemAgenda {
		return this.#_requestShedulerLocation(element);
	}

	/**
	 * Réinitialise le lieu à sa valeur par défaut.
	 * @returns L'instance du composant
	 */
	public resetLocation(): HTMLBnumCardItemAgenda {
		return this.#_requestShedulerLocation(SYMBOL_RESET);
	}

	/**
	 * Applique la logique d'affichage pour la date (aujourd'hui, demain, etc.).
	 * @param element Instance HTMLBnumDate à formater
	 * @returns Instance HTMLBnumDate modifiée
	 */
	public setDateLogic(element: HTMLBnumDate): HTMLBnumDate {
		element.formatEvent.add(EVENT_DEFAULT, param => {
			const now: Date = new Date();
			const date: Date =
				typeof param.date === 'string'
					? ((BnumDateUtils.parse(param.date, element.format) ??
							param.date) as Date)
					: (param.date as Date);

			if (BnumDateUtils.isSameDay(date, now)) param.date = FORMAT_TODAY;
			else if (BnumDateUtils.isSameDay(date, BnumDateUtils.addDays(now, 1)))
				param.date = FORMAT_TOMORROW;
			else
				// eslint-disable-next-line no-restricted-syntax
				param.date = CapitalizeLine(
					BnumDateUtils.format(
						date,
						BnumDateUtils.getOptionsFromToken(FORMAT_EVENT_DATE),
						element.localeElement,
					),
				);

			return param;
		});

		return element;
	}

	/**
	 * Applique la logique d'affichage pour l'heure (heure ou date selon le jour).
	 * @param element Instance HTMLBnumDate à formater
	 * @returns Instance HTMLBnumDate modifiée
	 */
	public setHourLogic(element: HTMLBnumDate): HTMLBnumDate {
		element.formatEvent.add(EVENT_DEFAULT, (param: { date: Date | string }) => {
			const date: Date =
				typeof param.date === 'string'
					? ((BnumDateUtils.parse(param.date, element.format) ??
							param.date) as Date)
					: (param.date as Date);

			if (BnumDateUtils.isSameDay(date, this.baseDate))
				param.date = BnumDateUtils.format(
					date,
					BnumDateUtils.getOptionsFromToken(FORMAT_HOUR_DEFAULT),
					element.localeElement,
				);
			else
				param.date = BnumDateUtils.format(
					date,
					BnumDateUtils.getOptionsFromToken(FORMAT_HOUR_DIFF_DAY),
					element.localeElement,
				);
			return param;
		});

		return element;
	}

	/**
	 * Ajoute un ou plusieurs modes au composant.
	 * @param modes Modes à ajouter.
	 */
	addOtherModes(...modes: string[]) {
		const currentModes = this.otherModes;
		const newModes = [...new Set([...currentModes, ...modes])];

		this.setAttribute(ATTRIBUTE_OTHER_MODES, newModes.join(','));
	}

	/**
	 * Retire un mode du composant.
	 * @param mode Mode à retirer.
	 */
	removeMode(mode: string) {
		const currentModes = this.otherModes;
		const newModes = currentModes.filter(m => m !== mode);
		this.setAttribute(ATTRIBUTE_OTHER_MODES, newModes.join(','));
	}
	//#endregion

	//#region Private Methods
	#_requestShedulerAction(
		element: HTMLElement | symbol,
		{ forceCall = false }: { forceCall?: boolean } = {},
	): this {
		this.#_shedulerAction ??= new Scheduler<HTMLElement | symbol>(
			innerElement => this.#_updateAction(innerElement!),
		);

		if (forceCall) this.#_shedulerAction.call(element);
		else this.#_shedulerAction.schedule(element);
		return this;
	}

	#_updateAction(element: HTMLElement | symbol): void {
		if (element === SYMBOL_RESET) {
			this._p_removeState(STATE_ACTION_DEFINED);
			this.#_resetItem(this.#_ui.overrideAction, this.#_ui.slotAction);
			return;
		}

		this._p_addState(STATE_ACTION_DEFINED);
		this.#_ui.overrideAction.innerHTML = EMPTY_STRING;
		this.#_ui.overrideAction.appendChild(element as HTMLElement);

		this.#_ui.slotAction.hidden = true;
		this.#_ui.overrideAction.hidden = false;
	}

	#_requestShedulerTitle(element: HTMLElement | string | symbol): this {
		this.#_shedulerTitle ??= new Scheduler<HTMLElement | string | symbol>(
			innerElement => this.#_updateTitle(innerElement!),
		);

		this.#_shedulerTitle.schedule(element);
		return this;
	}

	#_updateTitle(element: HTMLElement | string | symbol): void {
		if (element === SYMBOL_RESET) {
			this.#_resetItem(this.#_ui.overrideTitle, this.#_ui.slotTitle);
			return;
		}

		this.#_ui.overrideTitle.innerHTML = EMPTY_STRING;

		if (typeof element === 'string') {
			const textNode: Text = document.createTextNode(element);
			this.#_ui.overrideTitle.appendChild(textNode);
		} else {
			this.#_ui.overrideTitle.appendChild(element as HTMLElement);
		}

		this.#_ui.slotTitle.hidden = true;
		this.#_ui.overrideTitle.hidden = false;
	}

	#_requestShedulerLocation(element: HTMLElement | string | symbol): this {
		this.#_shedulerLocation ??= new Scheduler<HTMLElement | string | symbol>(
			innerElement => this.#_updateLocation(innerElement!),
		);

		this.#_shedulerLocation.schedule(element);
		return this;
	}

	#_updateLocation(element: HTMLElement | string | symbol): void {
		if (element === SYMBOL_RESET) {
			this.#_resetItem(this.#_ui.overrideLocation, this.#_ui.slotLocation);
			return;
		}

		this.#_ui.overrideLocation.innerHTML = EMPTY_STRING;

		if (typeof element === 'string') {
			const textNode: Text = document.createTextNode(element);
			this.#_ui.overrideLocation.appendChild(textNode);
		} else {
			this.#_ui.overrideLocation.appendChild(element as HTMLElement);
		}

		this.#_ui.slotLocation.hidden = true;
		this.#_ui.overrideLocation.hidden = false;
	}

	#_resetItem(
		action: HTMLElement,
		slot: HTMLSlotElement,
	): HTMLBnumCardItemAgenda {
		action.innerHTML = EMPTY_STRING;
		slot.hidden = false;
		action.hidden = true;

		return this;
	}
	#_slotEmpty(slot: HTMLSlotElement): boolean {
		return slot.assignedNodes().length === 0;
	}
	#_isSlotLocationEmpty(): boolean {
		return this.#_ui.slotLocation
			? this.#_slotEmpty(this.#_ui.slotLocation)
			: true;
	}
	#_generateDateHtml(startDate: Date): HTMLBnumDate {
		return this.setDateLogic(HTMLBnumDate.Create(startDate));
	}
	//#endregion

	//#region Static Methods
	/**
	 * Crée une nouvelle instance du composant agenda avec les paramètres donnés.
	 * @param baseDate Date de base
	 * @param startDate Date de début
	 * @param endDate Date de fin
	 * @param options Options supplémentaires (allDay, title, location, action)
	 * @returns Instance HTMLBnumCardItemAgenda
	 */
	public static Create(
		baseDate: Date,
		startDate: Date,
		endDate: Date,
		{
			allDay = false,
			title = null,
			location = null,
			action = null,
			isPrivate = false,
			mode = null,
		}: CreateConstructorOptions = {},
	): HTMLBnumCardItemAgenda {
		const node = document.createElement(this.TAG) as HTMLBnumCardItemAgenda;

		node.baseDate = baseDate;
		node.startDate = startDate;
		node.endDate = endDate;

		if (allDay) node.setAttribute(ATTRIBUTE_ALL_DAY, ATTRIBUTE_ALL_DAY);
		if (title) node.setAttribute(ATTRIBUTE_DATA_TITLE, title);
		if (location) node.setAttribute(ATTRIBUTE_DATA_LOCATION, location);

		if (isPrivate) node.setAttribute(ATTRIBUTE_PRIVATE, ATTRIBUTE_PRIVATE);
		if (mode) node.setAttribute(ATTRIBUTE_MODE, mode);

		if (action) {
			if (typeof action === 'function') node.onstartdefineaction.push(action);
			else
				node.onstartdefineaction.push(param => {
					param.action = action!.element;
					param.action!.onclick = action!.callback;
					return param;
				});
		}

		return node;
	}

	/**
	 * @inheritdoc
	 */
	static _p_observedAttributes(): string[] {
		return [
			...super._p_observedAttributes(),
			ATTRIBUTE_ALL_DAY,
			ATTRIBUTE_PRIVATE,
			ATTRIBUTE_MODE,
			ATTRIBUTE_OTHER_MODES,
		];
	}

	/**
	 * Crée une nouvelle instance du composant agenda à partir d'un objet événement.
	 * @param baseDate Date de base
	 * @param agendaEvent Objet événement source
	 * @param options Fonctions de sélection et action personnalisée
	 * @returns Instance HTMLBnumCardItemAgenda
	 */
	public static FromEvent(
		baseDate: Date,
		agendaEvent: AgendaEvent,
		{
			startDateSelector = null,
			endDateSelector = null,
			allDaySelector = null,
			titleSelector = null,
			locationSelector = null,
			action = null,
		}: FromEventConstructorOptions = {},
	): HTMLBnumCardItemAgenda {
		const [startDate, endDate] = this.#_tryGetAgendaDates(
			{
				val: agendaEvent.start,
				selector: startDateSelector,
			},
			{
				val: agendaEvent.end,
				selector: endDateSelector,
			},
		);

		const allDay: boolean =
			agendaEvent?.allDay ?? allDaySelector?.(agendaEvent) ?? false;

		const title: string =
			agendaEvent?.title ?? titleSelector?.(agendaEvent) ?? EMPTY_STRING;

		const location: string =
			agendaEvent?.location ?? locationSelector?.(agendaEvent) ?? EMPTY_STRING;

		return this.Create(baseDate, startDate, endDate, {
			allDay: allDay,
			title: title,
			location: location,
			action: action,
		});
	}

	/**
	 * Tente d'obtenir une date d'agenda à partir d'une valeur donnée.
	 * @param val La valeur à analyser.
	 * @param selector Une fonction de sélection pour extraire la date.
	 * @returns La date d'agenda ou une date invalide.
	 */
	static #_TryGetAgendaDate(
		val: UnknownDateElement,
		selector: Nullable<(obj: unknown) => Date>,
	): Date {
		return typeof val === 'string'
			? new Date(val)
			: val?.toDate
				? val.toDate()
				: (selector?.(val) ?? new Date('Date invalide'));
	}

	/**
	 * Tente d'obtenir une liste de dates d'agenda à partir des valeurs données.
	 * @param options Options contenant les valeurs et sélecteurs.
	 * @returns La liste des dates d'agenda.
	 */
	static #_tryGetAgendaDates(
		...options: {
			val: UnknownDateElement;
			selector: Nullable<(obj: unknown) => Date>;
		}[]
	): Date[] {
		return options.map(option =>
			this.#_TryGetAgendaDate(option.val, option.selector),
		);
	}
	//#endregion
}
